import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { User } from "../auth/useUser";
import { useSignOut } from "../auth/useSignOut";
import { RunMenu } from "./RunMenu";

// Shared top-bar across authenticated views. Logo on the left, user/run menu/settings/
// menu on the right. Sticky + backdrop-blurred so long content scrolls
// underneath. The breadcrumb slot lets per-view chrome insert its own
// "products / p_xyz" trail without the view rendering its own header.
//
// Width contract: the default `max-w-6xl` matches the Product page (the
// reference for chrome consistency). Every authenticated view should
// either accept the default or pass `max-w-6xl` explicitly. Graph view
// keeps the same header chrome; its body is the only place that breaks
// out to full viewport width.
//
// 0051 slice 2: one menu holds the page's own items (the product page: History, Map and
// the exports) and sign out, at every width.

// One item in the header menu: a route (`to`) or a download (`href`).
export type HeaderMenuItem = { label: string; to?: string; href?: string; download?: boolean };

export function Header({
  user,
  breadcrumb,
  maxWidthClass = "max-w-6xl",
  menuItems = [],
}: {
  user: User;
  breadcrumb?: React.ReactNode;
  maxWidthClass?: string;
  menuItems?: HeaderMenuItem[];
}) {
  return (
    <header className="border-b border-ink-800 bg-ink-950/70 backdrop-blur-sm sticky top-0 z-10">
      <div className={`${maxWidthClass} mx-auto px-4 sm:px-8 h-14 flex items-center justify-between gap-4`}>
        <div className="flex items-center gap-3 sm:gap-4 text-xs min-w-0">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-1.5 w-1.5 rounded-full bg-accent" />
            <span className="font-mono text-xs text-ink-200 tracking-widest uppercase">
              Eminity
            </span>
          </Link>
          <div className="hidden sm:flex items-center gap-4 min-w-0 text-xs">{breadcrumb}</div>
        </div>

        <div className="flex items-center gap-3 sm:gap-6 text-xs">
          <span className="hidden md:inline text-ink-300 font-medium">{user.display_name}</span>
          <RunMenu />
          <Link
            to="/settings"
            aria-label="Settings"
            title="Settings"
            className="flex items-center text-ink-400 hover:text-ink-200 transition-colors"
          >
            <SettingsCog />
          </Link>
          <HeaderMenu items={menuItems} />
        </div>
      </div>
    </header>
  );
}

// HeaderMenu: the page's own items, then sign out, behind one button visible
// at every width. It closes on an item, an outside click or Escape (the run
// menu's pattern).
function HeaderMenu({ items }: { items: HeaderMenuItem[] }) {
  const signOut = useSignOut();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const itemClass = "block w-full px-4 py-2 text-left font-mono text-[11px] text-ink-300 hover:bg-ink-900/60 hover:text-ink-100 transition-colors";

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Menu"
        aria-expanded={open}
        className="flex items-center px-1 text-ink-400 hover:text-ink-200 transition-colors"
      >
        <span aria-hidden="true" className="text-base leading-none">
          ⋯
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-30 w-48 overflow-hidden rounded-md border border-ink-800 bg-ink-950/95 py-1 shadow-2xl backdrop-blur-sm animate-fade-in">
          <ul>
            {items.map((item) => (
              <li key={item.label}>
                {item.to !== undefined ? (
                  <Link to={item.to} onClick={() => setOpen(false)} className={itemClass}>
                    {item.label}
                  </Link>
                ) : (
                  <a href={item.href} download={item.download} onClick={() => setOpen(false)} className={itemClass}>
                    {item.label}
                  </a>
                )}
              </li>
            ))}
            <li className={items.length > 0 ? "border-t border-ink-800 mt-1 pt-1" : undefined}>
              <button
                type="button"
                onClick={() => signOut.mutate()}
                disabled={signOut.isPending}
                className={`${itemClass} disabled:opacity-50`}
              >
                {signOut.isPending ? "signing out…" : "sign out"}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

// A real toothed gear (Feather "settings" cog) — reads unambiguously as
// settings, unlike a center-dot-plus-rays glyph which renders as a sun.
function SettingsCog() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
